import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, ShieldCheck, Cookie, FileSearch, Loader2, AlertCircle } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ReportView } from "../components/ReportView";
import { ScanAnimation } from "../components/ScanAnimation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LINES = [
    "Fetching company profile from the Compliance Graph",
    "Locating the privacy policy",
    "Reading policy clauses against Article 13 and 14",
    "Scanning cookies and consent behaviour",
    "Classifying trackers set before consent",
    "Grading and mapping gaps to services",
];

const LoadingState = ({ url }) => {
    const [step, setStep] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setStep((s) => Math.min(s + 1, STATUS_LINES.length - 1)), 2200);
        return () => clearInterval(t);
    }, []);
    return (
        <div className="max-w-2xl mx-auto px-4 py-14 text-center" data-testid="loading-state">
            <div className="w-[300px] max-w-full mx-auto">
                <ScanAnimation active />
            </div>
            <h2 className="text-2xl font-bold text-asphalt mt-4">Auditing {url}</h2>
            <p className="text-sm text-muted mt-1">This scan is thorough, it can take up to a minute.</p>
            <div className="mt-8 text-left max-w-md mx-auto space-y-2.5">
                {STATUS_LINES.map((line, i) => (
                    <div key={line} className={`flex items-center gap-3 text-sm transition-opacity ${i <= step ? "opacity-100" : "opacity-40"}`}>
                        {i < step ? <ShieldCheck className="w-4 h-4 text-[#22AA78]" />
                            : i === step ? <Loader2 className="w-4 h-4 text-aqua gdpr-spin" />
                                : <div className="w-4 h-4 rounded-full border border-line" />}
                        <span className={i <= step ? "text-asphalt" : "text-muted"}>{line}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const Landing = ({ onSubmit }) => {
    const [value, setValue] = useState("");
    return (
        <div className="relative overflow-hidden">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20">
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-center">
                    <div className="text-center lg:text-left">
                        <span className="inline-block text-xs font-semibold uppercase tracking-wider text-asphalt bg-aqua-tint px-3 py-1 rounded-full">Free assessment - Instant results</span>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-asphalt mt-6 leading-tight">
                            Privacy Policy and<br />Cookie Auditor
                        </h1>
                        <p className="text-base md:text-lg text-muted mt-5 max-w-2xl mx-auto lg:mx-0">
                            Grade any website's privacy policy and cookie setup against GDPR, UK GDPR and ePrivacy / PECR. Get a gap by gap report in under a minute.
                        </p>
                        <form onSubmit={(e) => { e.preventDefault(); if (value.trim()) onSubmit(value.trim()); }}
                            className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto lg:mx-0">
                            <div className="flex-1 flex items-center gap-2 bg-white border border-line rounded-md px-4 focus-within:border-aqua transition-colors">
                                <Search className="w-5 h-5 text-muted shrink-0" />
                                <input value={value} onChange={(e) => setValue(e.target.value)}
                                    data-testid="url-input"
                                    placeholder="example.com"
                                    className="w-full py-3.5 outline-none text-asphalt bg-transparent" />
                            </div>
                            <button type="submit" data-testid="audit-submit"
                                className="px-7 py-3.5 rounded-md bg-aqua text-asphalt font-medium hover:brightness-95 transition-all whitespace-nowrap">
                                Run free audit
                            </button>
                        </form>
                        <p className="text-xs text-muted mt-3">Indicative guidance, not legal advice.</p>
                    </div>

                    <div className="order-first lg:order-last flex justify-center gdpr-fade-up">
                        <ScanAnimation />
                    </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-5 mt-16 text-left">
                    {[
                        { Icon: FileSearch, t: "Clause-level grading", d: "Every Article 13 and 14 disclosure checked with quoted evidence." },
                        { Icon: Cookie, t: "Live cookie scan", d: "A real browser checks if cookies are set prior to consent." },
                        { Icon: ShieldCheck, t: "Mapped to fixes", d: "Practical resolutions set out for every gap." },
                    ].map((f, i) => (
                        <div key={f.t} className="bg-white border border-line rounded-lg p-6 gdpr-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
                            <f.Icon className="w-6 h-6 text-aqua" />
                            <h3 className="font-bold text-asphalt mt-3">{f.t}</h3>
                            <p className="text-sm text-muted mt-1">{f.d}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default function Home() {
    const [params, setParams] = useSearchParams();
    const [state, setState] = useState("idle"); // idle | loading | done | error
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const activeUrl = useRef("");

    const urlParam = params.get("url");
    const refresh = params.get("refresh");

    const runAudit = useCallback(async (url) => {
        activeUrl.current = url;
        setState("loading");
        setError("");
        try {
            const res = await axios.get(`${API}/page-audit`, { params: { url, refresh: refresh === "1" ? 1 : 0 } });
            setData(res.data);
            setState("done");
            if (res.data?.meta?.company_name) {
                document.title = `${res.data.meta.company_name} audit | GDPRLocal`;
            }
        } catch (e) {
            const msg = e.response?.status === 429
                ? "Too many audits from your network right now. Please wait a moment and retry."
                : e.response?.data?.detail || "The site could not be audited. Please check the URL and try again.";
            setError(msg);
            setState("error");
        }
    }, [refresh]);

    // auto-start from ?url=
    useEffect(() => {
        if (urlParam && urlParam !== activeUrl.current) {
            runAudit(urlParam);
        }
        if (!urlParam) {
            setState("idle");
            setData(null);
        }
    }, [urlParam, runAudit]);

    const handleSubmit = (url) => {
        setParams({ url });
    };

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <Header />
            <main className="flex-1">
                {state === "idle" && <Landing onSubmit={handleSubmit} />}
                {state === "loading" && <LoadingState url={activeUrl.current} />}
                {state === "error" && (
                    <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="error-state">
                        <AlertCircle className="w-10 h-10 text-danger mx-auto" />
                        <h2 className="text-2xl font-bold text-asphalt mt-4">Could not complete the audit</h2>
                        <p className="text-muted mt-2">{error}</p>
                        <button onClick={() => setParams({})} className="mt-6 px-6 py-3 rounded-md bg-aqua text-asphalt font-medium">Back to start</button>
                    </div>
                )}
                {state === "done" && data && <ReportView data={data} />}
            </main>
            <Footer />
        </div>
    );
}
